/**
 * components/BookMeetingModal.jsx
 * Global Analytos lead-capture modal ("Book Meeting") wrapping EnquiryForm.
 * Opened by landing-page CTAs with a per-surface `product` string so the
 * shared Leads sheet can attribute conversions.
 *
 * Scroll-lock note: the landing page manages html/body overflow itself, so
 * this modal SAVES the previous overflow values on open and restores those
 * exact values on close (never blindly resets to '').
 */

import React, { useEffect, useRef } from 'react';
import EnquiryForm from './EnquiryForm';

export default function BookMeetingModal({ product, onClose }) {
  const panelRef = useRef(null);

  // Save + restore previous overflow (landing page controls it on mount)
  useEffect(() => {
    const html = document.documentElement, body = document.body;
    const prevHtml = html.style.overflow, prevBody = body.style.overflow;
    html.style.overflow = 'hidden'; body.style.overflow = 'hidden';
    return () => { html.style.overflow = prevHtml; body.style.overflow = prevBody; };
  }, []);

  // Esc to close + focus first input
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const t = setTimeout(() => panelRef.current?.querySelector('input')?.focus(), 60);
    return () => { window.removeEventListener('keydown', onKey); clearTimeout(t); };
  }, [onClose]);

  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 500,
        background: 'rgba(4,6,10,0.72)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
      role="dialog" aria-modal="true" aria-label="Book a meeting"
    >
      <div ref={panelRef} style={{
        width: 'min(560px, 100%)', maxHeight: '90vh', overflowY: 'auto',
        background: '#FFFFFF', border: '1px solid #EDE8E1',
        borderRadius: 16, padding: '26px 26px 22px', position: 'relative',
        boxShadow: '0 34px 90px rgba(0,0,0,0.45)',
      }}>
        <button
          onClick={onClose} aria-label="Close"
          style={{
            position: 'absolute', top: 14, right: 14, width: 30, height: 30,
            borderRadius: 8, border: '1px solid #EDE8E1',
            background: '#FAFAF9', color: '#6b6353',
            cursor: 'pointer', fontSize: 14, lineHeight: 1,
          }}
        >✕</button>
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 21, fontWeight: 800, color: '#1A1410', letterSpacing: '-0.02em' }}>
            Let's start a conversation
          </div>
          <div style={{ fontSize: 13.5, color: '#6b6353', marginTop: 5 }}>
            Tell us about your project and we'll get back to you shortly.
          </div>
        </div>
        <EnquiryForm product={product} />
      </div>
    </div>
  );
}
